import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Tab,
    Tabs,
    TextField,
    Grid,
} from "@mui/material";
import { columns, Get_URI } from "./config";
import CustomDataGrid from "../../Components/CustomDataGrid";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import { Link, useNavigate } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RemoveRedEye from "@mui/icons-material/RemoveRedEye";
import { toast } from "react-toastify";
import { REACT_APP_BACKEND_URL } from "../../Utils/Language/urlBase";

const SalesList = () => {
    const [tabIndex, setTabIndex] = useState(0); // Tab state
    const [isRefresh, setIsRefresh] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedSaleId, setSelectedSaleId] = useState(null);
    const [regularSales, setRegularSales] = useState([]); // Sales with Type ID 1 & 2
    const [hiddenSales, setHiddenSales] = useState([]); // Sales with Type ID 3
    const [searchAmount, setSearchAmount] = useState("");
    const [searchCustomer, setSearchCustomer] = useState("");
    const authHeader = useAuthHeader();
    const navigate = useNavigate();

    // Tab Change Handler
    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    const actions = [
        {
            title: "View",
            color: "info",
            handler: (id) => {
                navigate("/Invoice/" + id);
            },
            parameters: ["id"],
            icon: <RemoveRedEye color="info" />,
        },
        {
            title: "Edit",
            color: "primary",
            handler: (id) => {
                navigate("/EditInvoice/" + id);
            },
            parameters: ["id"],
            icon: <EditIcon color="primary" />,
        },
        {
            title: "Delete",
            color: "error",
            handler: (id) => {
                setSelectedSaleId(id);
                setOpenDeleteDialog(true);
            },
            parameters: ["id"],
            icon: <DeleteIcon />,
        },
    ];

    // Fetch Sales
    useEffect(() => {
        const getSales = async () => {
            try {
                const response = await fetch(Get_URI("GET"), {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authHeader,
                    },
                });
                if (response.ok) {
                    const responseData = await response.json();

                    // Filter sales by type
                    const regular = responseData.data.filter(
                        (sale) => sale.typeId === 1 || sale.typeId === 2
                    );
                    const hidden = responseData.data.filter(
                        (sale) => sale.typeId === 3
                    );

                    setRegularSales(regular);
                    setHiddenSales(hidden);
                } else {
                    throw new Error("Network response was not ok");
                }
            } catch (err) {
                toast.error("Something went wrong getting sales");
            }
        };
        getSales();
    }, [authHeader, isRefresh]);

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setSelectedSaleId(null);
    };

    const handleConfirmDelete = async () => {
        const response = await fetch(
            `${REACT_APP_BACKEND_URL}/invoices/${selectedSaleId}`,
            {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: authHeader,
                },
            }
        );

        if (response.ok) {
            toast.success("Sale deleted successfully");
            setIsRefresh(!isRefresh);
            setOpenDeleteDialog(false);
            setSelectedSaleId(null);
        } else {
            toast.error("Failed to delete sale");
        }
    };

    // Filter sales based on search criteria
    const filterSales = (sales) => {
        return sales.filter((sale) => {
            const customerName = sale.OtherParty?.name?.toLowerCase() || "";
            const customerSearch = searchCustomer.toLowerCase();
            const matchesCustomer = customerName.includes(customerSearch);

            const totalAmount = sale.totalAmount?.toString() || "";
            const matchesAmount =
                searchAmount === "" || totalAmount.includes(searchAmount);

            return matchesCustomer && matchesAmount;
        });
    };

    const filteredRegularSales = filterSales(regularSales);
    const filteredHiddenSales = filterSales(hiddenSales);

    return (
        <Box sx={{ width: "100%" }}>
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                }}
            >
                <Typography
                    variant="h4"
                    sx={{ fontWeight: "bold" }}
                    color="primary"
                    gutterBottom
                >
                    Sales List
                </Typography>
                <Link to="/AddSale" variant="contained" color="primary">
                    Add New Invoice
                </Link>
            </Box>

            {/* Tabs */}
            <Tabs value={tabIndex} onChange={handleTabChange} centered>
                <Tab label="Regular Sales" />
                <Tab label="Hidden Sales" />
            </Tabs>

            {/* Search Fields */}
            <Box sx={{ mt: 2, mb: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Search by customer name"
                            variant="outlined"
                            value={searchCustomer}
                            onChange={(e) => setSearchCustomer(e.target.value)}
                            placeholder="Enter customer name..."
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Search by amount"
                            variant="outlined"
                            value={searchAmount}
                            onChange={(e) => setSearchAmount(e.target.value)}
                            placeholder="Enter amount..."
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* Tab Panels */}
            <Box sx={{ mt: 2 }}>
                {tabIndex === 0 && (
                    <CustomDataGrid
                        handleSaveConfirmClick={() => {
                            console.log("Regular Sales Save Confirm Clicked");
                        }}
                        hasEdit={0}
                        columns={columns}
                        rows={filteredRegularSales} // Filtered Regular Sales
                        actions={actions}
                    />
                )}
                {tabIndex === 1 && (
                    <CustomDataGrid
                        handleSaveConfirmClick={() => {
                            console.log("Hidden Sales Save Confirm Clicked");
                        }}
                        hasEdit={0}
                        columns={columns}
                        rows={filteredHiddenSales} // Filtered Hidden Sales
                        actions={actions}
                    />
                )}
            </Box>

            {/* Delete Dialog */}
            <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this row?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SalesList;
